/**
 * Pistes : génération du tracé, mesure (longueur, dénivelé, pente) et
 * classement en difficulté, plus la trajectoire des skieurs.
 *
 * Le tracé est calculé une seule fois par le serveur (à la construction) puis
 * transmis : le client ne le régénère pas. La trajectoire d'un skieur, elle,
 * est recalculée par le client à chaque image à partir de son mouvement.
 */
import { chaikin, dist2, Path3, resample, type Vec2, type Vec3 } from './geometry';
import type { Heightfield } from './terrain';
import type { Difficulty, PisteSize } from './state';

export const PISTE_WIDTH: Record<Difficulty, number> = { verte: 36, bleue: 30, rouge: 26, noire: 22 };

/** Facteur de largeur selon le gabarit choisi au tracé. */
export const PISTE_SIZE_FACTOR: Record<PisteSize, number> = { narrow: 0.7, normal: 1, wide: 1.35 };

export const PISTE_SIZE_LABEL: Record<PisteSize, string> = { narrow: 'Étroite', normal: 'Normale', wide: 'Large' };

/** Largeur (m) d'une piste selon sa difficulté et son gabarit. */
export function pisteWidth(difficulty: Difficulty, size: PisteSize = 'normal'): number {
  return Math.round(PISTE_WIDTH[difficulty] * PISTE_SIZE_FACTOR[size]);
}

export const DIFFICULTY_LABEL: Record<Difficulty, string> = { verte: 'Verte', bleue: 'Bleue', rouge: 'Rouge', noire: 'Noire' };

/**
 * Seuils entre difficultés, appliqués au score de pente :
 * moyenne de la pente globale et du 90e centile des pentes sur 40 m.
 */
export const DIFFICULTY_SLOPES = { bleue: 0.215, rouge: 0.3, noire: 0.4 } as const;

export function slopeScore(avgSlope: number, p90: number): number {
  return 0.5 * avgSlope + 0.5 * p90;
}

export function classifySlope(score: number): Difficulty {
  if (score >= DIFFICULTY_SLOPES.noire) return 'noire';
  if (score >= DIFFICULTY_SLOPES.rouge) return 'rouge';
  if (score >= DIFFICULTY_SLOPES.bleue) return 'bleue';
  return 'verte';
}

export interface PisteAnalysis {
  length: number;
  drop: number;
  avgSlope: number;
  p90Slope: number;
  maxSlope: number;
  /** Plus forte remontée cumulée (m) : un tracé qui remonte est refusé. */
  uphill: number;
  difficulty: Difficulty;
}

export function analysePiste(hf: Heightfield, path: readonly Vec2[]): PisteAnalysis {
  const pts = resample(path, 10);
  const hs = pts.map((p) => hf.height(p[0], p[1]));
  let length = 0;
  for (let i = 1; i < pts.length; i++) {
    const h = dist2(pts[i - 1] as Vec2, pts[i] as Vec2);
    length += Math.hypot(h, (hs[i] as number) - (hs[i - 1] as number));
  }
  const slopes: number[] = [];
  const w = 4;
  for (let i = 0; i + w < pts.length; i++) {
    const hd = dist2(pts[i] as Vec2, pts[i + w] as Vec2) || 1;
    slopes.push(((hs[i] as number) - (hs[i + w] as number)) / hd);
  }
  slopes.sort((p, q) => p - q);
  const p90 = slopes.length ? (slopes[Math.min(slopes.length - 1, Math.floor(slopes.length * 0.9))] as number) : 0;
  const maxSlope = slopes.length ? (slopes[slopes.length - 1] as number) : 0;
  let uphill = 0;
  let minSoFar = hs[0] as number;
  for (const h of hs) {
    uphill = Math.max(uphill, h - minSoFar);
    minSoFar = Math.min(minSoFar, h);
  }
  const drop = (hs[0] as number) - (hs[hs.length - 1] as number);
  const horiz = pts.reduce((acc, p, i) => (i ? acc + dist2(pts[i - 1] as Vec2, p) : 0), 0) || 1;
  const avgSlope = drop / horiz;
  return {
    length,
    drop,
    avgSlope,
    p90Slope: p90,
    maxSlope,
    uphill,
    difficulty: classifySlope(slopeScore(avgSlope, p90)),
  };
}

export interface PisteGenOptions {
  /** Côté de la première traversée (+1 ou -1) par rapport à l'axe départ -> arrivée. */
  side: 1 | -1;
  /** Pente moyenne visée (0,15 verte ... 0,40 noire). */
  grade: number;
  seed: number;
  /** Segments à éviter (lignes de remontées), avec distance minimale. */
  avoid?: { a: Vec2; b: Vec2; clearance: number }[];
}

/** Pente visée par difficulté souhaitée. */
export const TARGET_GRADE: Record<Difficulty, number> = { verte: 0.15, bleue: 0.22, rouge: 0.3, noire: 0.42 };

/**
 * Tracé d'une piste du point `from` (haut) au point `to` (bas), à la manière
 * d'un tracé de route : à chaque pas, on choisit la direction dont la pente
 * est la plus proche de la pente visée, tout en convergeant vers l'arrivée.
 * Sur un terrain trop raide, le tracé fait des traversées ; sur un terrain
 * plus doux que la cible, il descend droit. Le résultat est lissé.
 */
export function generatePistePath(hf: Heightfield, from: Vec2, to: Vec2, opts: PisteGenOptions): Vec2[] {
  const step = 12;
  const D0 = dist2(from, to);
  const ax: Vec2 = [(to[0] - from[0]) / D0, (to[1] - from[1]) / D0];
  // Normale de l'axe, orientée vers le côté demandé.
  const nrm: Vec2 = [ax[1] * opts.side, -ax[0] * opts.side];
  const pts: Vec2[] = [from];
  let pos: Vec2 = from;
  let prevHeading = Math.atan2(ax[0], ax[1]);
  const deg = Math.PI / 180;
  for (let k = 0; k < 400; k++) {
    const d = dist2(pos, to);
    if (d < step * 1.5) break;
    const toT = Math.atan2(to[0] - pos[0], to[1] - pos[1]);
    const hPos = hf.height(pos[0], pos[1]);
    const need = (hPos - hf.height(to[0], to[1])) / d;
    const desired = Math.max(0.04, Math.min(opts.grade, need));
    const maxDev = (d > 320 ? 80 : d > 160 ? 60 : d > 70 ? 35 : 10) * deg;
    const progress = 1 - d / D0;
    let best: Vec2 | null = null;
    let bestHeading = toT;
    let bestCost = Infinity;
    for (let a = -maxDev; a <= maxDev + 1e-9; a += 7.5 * deg) {
      const th = toT + a;
      const c: Vec2 = [pos[0] + step * Math.sin(th), pos[1] + step * Math.cos(th)];
      if (!hf.inBounds(c[0], c[1], 12)) continue;
      const g = (hPos - hf.height(c[0], c[1])) / step;
      let cost = 6 * Math.abs(g - desired) + 0.5 * Math.abs(a);
      if (g < 0) cost += 6 * -g;
      // Côté imposé : rester du bon côté de l'axe (ne pas recouper la ligne).
      const lateral = (c[0] - from[0]) * nrm[0] + (c[1] - from[1]) * nrm[1];
      if (lateral < -6) cost += 1.5 + 0.05 * -lateral;
      if (progress < 0.35) cost -= 0.25 * Math.max(0, Math.min(1, lateral / 60));
      // Virages pas trop secs.
      let turn = Math.abs(th - prevHeading);
      if (turn > Math.PI) turn = 2 * Math.PI - turn;
      if (turn > 55 * deg) cost += 2 * (turn - 55 * deg);
      for (const av of opts.avoid ?? []) {
        const dd = distPointSegmentLocal(c, av.a, av.b);
        if (dd < av.clearance) cost += 3 * (1 - dd / av.clearance) + 1;
      }
      if (cost < bestCost) {
        bestCost = cost;
        best = c;
        bestHeading = th;
      }
    }
    if (!best) break;
    pts.push(best);
    pos = best;
    prevHeading = bestHeading;
  }
  pts.push(to);
  const smooth = chaikin(pts, 3);
  return resample(smooth, 14).map((p) => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10] as Vec2);
}

function distPointSegmentLocal(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const l2 = dx * dx + dz * dz;
  let t = l2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dz * t));
}

/* ------------------------------------------------------------------ */
/* Trajectoire des skieurs                                              */
/* ------------------------------------------------------------------ */

/** Piste prête pour l'échantillonnage (polyligne posée sur le relief). */
export interface PisteTrack {
  id: number;
  path: Path3;
  width: number;
  length: number;
}

export function makePisteTrack(hf: Heightfield, id: number, path: readonly Vec2[], width: number): PisteTrack {
  const pts = resample(path, 6).map((p): Vec3 => [p[0], hf.height(p[0], p[1]), p[1]]);
  const p3 = new Path3(pts);
  return { id, path: p3, width, length: p3.length };
}

/** Vitesse moyenne (m/s) selon le niveau (0 débutant, 1 confirmé, 2 expert). */
export function skierSpeed(skill: number, difficulty: Difficulty, quality: number): number {
  const table: Record<Difficulty, [number, number, number]> = {
    verte: [6, 9, 12],
    bleue: [5, 9, 13],
    rouge: [4, 7.5, 14],
    noire: [3.5, 6, 13],
  };
  const v = table[difficulty][Math.max(0, Math.min(2, skill))] as number;
  return v * (0.8 + 0.2 * quality);
}

/** Adéquation niveau / difficulté (0 = à éviter, 1 = idéal). */
export function pisteSuitability(skill: number, difficulty: Difficulty): number {
  const m: Record<Difficulty, [number, number, number]> = {
    verte: [1, 0.55, 0.2],
    bleue: [0.45, 1, 0.55],
    rouge: [0.05, 0.7, 1],
    noire: [0, 0.25, 1],
  };
  return m[difficulty][Math.max(0, Math.min(2, skill))] as number;
}

const tmp = new Float64Array(5);

/**
 * Position d'un skieur sur une piste : abscisse le long du tracé + écart
 * latéral sinusoïdal (virages). Écrit [x, y, z, cap, inclinaison] dans out.
 * La descente couvre la portion [s0, s1] de la trajectoire (toute la piste
 * par défaut) : un skieur entre ou quitte la piste à un raccord.
 */
export function sampleSki(
  hf: Heightfield,
  track: PisteTrack,
  t0: number,
  dur: number,
  lane: number,
  phase: number,
  skill: number,
  t: number,
  out: Float64Array | number[],
  s0 = 0,
  s1 = track.length,
): void {
  const u = dur > 0 ? Math.min(1, Math.max(0, (t - t0) / dur)) : 1;
  const a = Math.max(0, Math.min(track.length, s0));
  const b = Math.max(a, Math.min(track.length, s1));
  const s = a + u * (b - a);
  track.path.sample(s, tmp);
  const half = track.width * 0.5;
  const wavelength = skill === 2 ? 30 : skill === 1 ? 22 : 26;
  const amp = skill === 2 ? 7 : skill === 1 ? 6 : 4.5;
  // Entrée et sortie progressives : départ et arrivée au centre du tracé (raccords compris).
  const ramp = Math.min(1, (s - a) / 18, (b - s) / 18);
  const w = Math.max(0, ramp);
  const k = (2 * Math.PI) / wavelength;
  const ang = phase + s * k;
  let lateral = (lane * half * 0.5 + amp * Math.sin(ang)) * w;
  const lim = half * 0.85;
  if (lateral > lim) lateral = lim;
  else if (lateral < -lim) lateral = -lim;
  const dx = tmp[3] as number;
  const dz = tmp[4] as number;
  // Normale latérale (droite de la direction de descente).
  const nx = dz;
  const nz = -dx;
  const x = (tmp[0] as number) + nx * lateral;
  const z = (tmp[2] as number) + nz * lateral;
  const dLat = amp * k * Math.cos(ang) * w;
  const heading = Math.atan2(dx, dz) + Math.atan(dLat);
  out[0] = x;
  out[1] = hf.height(x, z);
  out[2] = z;
  out[3] = heading;
  out[4] = -Math.sin(ang) * 0.35 * w * (skill === 0 ? 0.4 : 1);
}
